import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, CheckCircle, Clock, XCircle, TrendingUp, TrendingDown, Activity, Users, Key } from 'lucide-react';

const TradingMonitor = () => {
  const [sessions, setSessions] = useState([]);
  const [settings, setSettings] = useState({ execution_mode: 'limit' });
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionLogs, setSessionLogs] = useState(null);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchSessions();
    fetchSettings();
    fetchCurrentPrice();
    
    const sessionsInterval = setInterval(fetchSessions, 10000); // Refresh every 10 seconds
    const priceInterval = setInterval(fetchCurrentPrice, 2000); // Update price every 2 seconds
    
    return () => {
      clearInterval(sessionsInterval);
      clearInterval(priceInterval);
    };
  }, []);

  const fetchCurrentPrice = async () => {
    try {
      const response = await axios.get('https://www.okx.com/api/v5/market/ticker?instId=BTC-USDT');
      if (response.data.code === '0' && response.data.data) {
        setCurrentPrice(parseFloat(response.data.data[0].last));
      }
    } catch (error) {
      console.error('Error fetching price:', error);
    } finally {
      setPriceLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/admin/trading-sessions?status=active`, {
        withCredentials: true
      });
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/admin/settings`, {
        withCredentials: true
      });
      console.log('Settings loaded:', response.data);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const updateExecutionMode = async (mode) => {
    try {
      const response = await axios.post(
        `${backendUrl}/api/admin/settings`,
        { execution_mode: mode },
        { withCredentials: true }
      );
      
      console.log('Settings update response:', response.data);
      
      // Update local state with the actual settings from server
      if (response.data.settings) {
        setSettings(response.data.settings);
      } else {
        setSettings({ execution_mode: mode });
      }
      
      alert(`✅ Режим исполнения изменен на: ${mode === 'limit' ? 'Лимитные ордера' : 'Скрытые ордера (Market)'}`);
      
      // Refresh settings from server to confirm
      setTimeout(fetchSettings, 500);
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('❌ Ошибка при обновлении настроек');
    }
  };

  const fetchSessionLogs = async (sessionId) => {
    try {
      const response = await axios.get(`${backendUrl}/api/admin/trading-sessions/${sessionId}/logs`, {
        withCredentials: true
      });
      setSessionLogs(response.data);
    } catch (error) {
      console.error('Error fetching session logs:', error);
    }
  };

  const getStepStatusBadge = (step, sessionStepIndex) => {
    if (sessionStepIndex > step) {
      return <Badge className="bg-green-500">Завершен</Badge>;
    } else if (sessionStepIndex === step) {
      return <Badge className="bg-yellow-500">В процессе</Badge>;
    } else {
      return <Badge className="bg-gray-500">Ожидание</Badge>;
    }
  };

  const calculateDistanceToEntry = (session) => {
    if (!currentPrice || !session.setup?.steps) return null;
    
    const currentStep = session.setup.steps[session.current_step_index];
    if (!currentStep) return null;
    
    const entryPrice = currentStep.entry_price;
    const diff = entryPrice - currentPrice;
    const diffPercent = (diff / currentPrice) * 100;
    
    return {
      price: entryPrice,
      diff: diff,
      diffPercent: diffPercent,
      direction: currentStep.direction
    };
  };

  // Calculate statistics
  const stats = {
    totalSessions: sessions.length,
    uniqueUsers: new Set(sessions.map(s => s.user_id)).size,
    uniqueKeys: new Set(sessions.map(s => s.connection_id)).size,
    keysList: Array.from(new Set(sessions.map(s => s.connection?.key_name || 'Unknown'))),
  };

  // Debug logging
  if (sessions.length > 0) {
    console.log('=== Trading Monitor Debug ===');
    console.log('Total sessions:', sessions.length);
    console.log('Sessions details:', sessions.map(s => ({
      session_id: s.id?.substring(0, 8),
      user_email: s.user?.email,
      connection_id: s.connection_id?.substring(0, 8),
      key_name: s.connection?.key_name,
      exchange: s.exchange_name
    })));
    
    const connectionIds = sessions.map(s => s.connection_id);
    console.log('All connection_ids:', connectionIds);
    console.log('Unique connection_ids:', Array.from(new Set(connectionIds)));
    console.log('Unique keys count:', new Set(connectionIds).size);
    console.log('Keys list:', stats.keysList);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0a0a0a]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00ff88]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header with Price */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Мониторинг торговли</h1>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-[#00ff88]" />
                <span className="text-gray-400">Цена BTC:</span>
                <span className="text-2xl font-bold text-white font-mono">
                  ${priceLoading ? '...' : currentPrice?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                {!priceLoading && (
                  <Badge className="bg-green-500 animate-pulse">Live</Badge>
                )}
              </div>
            </div>
          </div>
          <Button
            onClick={() => {
              fetchSessions();
              fetchCurrentPrice();
            }}
            className="bg-gray-800 hover:bg-gray-700"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Обновить
          </Button>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#00ff88]/20 flex items-center justify-center">
                  <Activity className="h-6 w-6 text-[#00ff88]" />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Активные сессии</p>
                  <p className="text-3xl font-bold text-white">{stats.totalSessions}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Users className="h-6 w-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Уникальных пользователей</p>
                  <p className="text-3xl font-bold text-white">{stats.uniqueUsers}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <Key className="h-6 w-6 text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Активных ключей</p>
                  <p className="text-3xl font-bold text-white">{stats.uniqueKeys}</p>
                  {stats.keysList.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {stats.keysList.join(', ')}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Execution Mode Settings */}
        <Card className="bg-[#1a1a1a] border-gray-800 mb-6">
          <CardHeader>
            <CardTitle className="text-white">Настройки исполнения</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <p className="text-gray-400">Режим исполнения ордеров:</p>
              <div className="flex gap-2">
                <Button
                  onClick={() => updateExecutionMode('limit')}
                  className={`${
                    settings.execution_mode === 'limit'
                      ? 'bg-[#00ff88] text-black'
                      : 'bg-gray-700 text-white'
                  }`}
                >
                  Лимитные ордера
                </Button>
                <Button
                  onClick={() => updateExecutionMode('market')}
                  className={`${
                    settings.execution_mode === 'market'
                      ? 'bg-[#00ff88] text-black'
                      : 'bg-gray-700 text-white'
                  }`}
                >
                  Скрытые ордера (Market)
                </Button>
              </div>
              <Badge className={settings.execution_mode === 'limit' ? 'bg-blue-500' : 'bg-orange-500'}>
                Активен: {settings.execution_mode === 'limit' ? 'Limit' : 'Market'}
              </Badge>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              {settings.execution_mode === 'limit'
                ? '📌 Лимитные ордера: Выставляются по указанной цене и ожидают исполнения'
                : '⚡ Скрытые ордера: Система мониторит цену и исполняет market ордера при достижении целевой цены'}
            </p>
          </CardContent>
        </Card>

        {/* Active Sessions */}
        <div className="grid gap-4">
          {sessions.length === 0 ? (
            <Card className="bg-[#1a1a1a] border-gray-800">
              <CardContent className="pt-6 text-center text-gray-400">
                <p>Нет активных торговых сессий</p>
              </CardContent>
            </Card>
          ) : (
            sessions.map((session) => {
              const distanceInfo = calculateDistanceToEntry(session);
              
              return (
                <Card key={session.id} className="bg-[#1a1a1a] border-gray-800">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-white text-lg">{session.setup?.title}</CardTitle>
                        <p className="text-sm text-gray-400">
                          Пользователь: {session.user?.name} ({session.user?.email})
                        </p>
                      </div>
                      <Badge className="bg-green-500 animate-pulse">Активна</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Distance to Entry */}
                    {distanceInfo && (
                      <div className={`rounded-lg p-4 mb-4 border ${
                        Math.abs(distanceInfo.diffPercent) < 0.5
                          ? 'bg-green-500/10 border-green-500/30'
                          : Math.abs(distanceInfo.diffPercent) < 2
                          ? 'bg-yellow-500/10 border-yellow-500/30'
                          : 'bg-gray-900/50 border-gray-800'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-400 mb-1">Расстояние до Entry:</p>
                            <div className="flex items-center gap-2">
                              {distanceInfo.direction === 'long' ? (
                                <TrendingUp className="h-5 w-5 text-green-400" />
                              ) : (
                                <TrendingDown className="h-5 w-5 text-red-400" />
                              )}
                              <span className="text-lg font-bold text-white">
                                ${Math.abs(distanceInfo.diff).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                              <span className={`text-lg font-bold ${
                                Math.abs(distanceInfo.diffPercent) < 0.5 ? 'text-green-400' : 
                                Math.abs(distanceInfo.diffPercent) < 2 ? 'text-yellow-400' : 
                                'text-gray-400'
                              }`}>
                                ({distanceInfo.diffPercent > 0 ? '+' : ''}{distanceInfo.diffPercent.toFixed(2)}%)
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-400">Entry цена:</p>
                            <p className="text-lg font-bold text-white font-mono">
                              ${distanceInfo.price.toLocaleString()}
                            </p>
                          </div>
                        </div>
                        {Math.abs(distanceInfo.diffPercent) < 0.5 && (
                          <div className="mt-2 flex items-center gap-2 text-green-400 text-sm">
                            <Activity className="h-4 w-4 animate-pulse" />
                            <span className="font-semibold">Цена близко к entry! Ожидаем исполнение...</span>
                          </div>
                        )}
                        {/* Warning if price missed entry for first step */}
                        {session.current_step_index === 0 && distanceInfo && (
                          (distanceInfo.direction === 'long' && currentPrice > distanceInfo.price) ||
                          (distanceInfo.direction === 'short' && currentPrice < distanceInfo.price)
                        ) && (
                          <div className="mt-2 flex items-center gap-2 text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded p-2">
                            <XCircle className="h-4 w-4" />
                            <span className="font-semibold">
                              ⚠️ Цена пропустила entry точку! 
                              {distanceInfo.direction === 'long' 
                                ? ` Цена выше entry на $${(currentPrice - distanceInfo.price).toFixed(2)}`
                                : ` Цена ниже entry на $${(distanceInfo.price - currentPrice).toFixed(2)}`
                              }
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-400">Биржа</p>
                        <p className="text-white font-semibold">
                          {session.exchange_name === 'binance' ? 'Binance' : session.exchange_name.toUpperCase()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Ключ</p>
                        <p className="text-[#00ff88] font-semibold">{session.connection?.key_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Текущий шаг</p>
                        <p className="text-white font-semibold">
                          {session.current_step_index + 1} / {session.setup?.steps?.length || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Прогресс</p>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-700 rounded-full h-2">
                            <div
                              className="bg-[#00ff88] h-2 rounded-full transition-all"
                              style={{
                                width: `${
                                  ((session.current_step_index + 1) / (session.setup?.steps?.length || 1)) * 100
                                }%`
                              }}
                            ></div>
                          </div>
                          <span className="text-white text-sm">
                            {Math.round(
                              ((session.current_step_index + 1) / (session.setup?.steps?.length || 1)) * 100
                            )}%
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Current Order Status */}
                    {session.current_order_id && (
                      <div className="bg-gray-900/50 rounded-lg p-4 mb-4">
                        <h4 className="text-white font-semibold mb-2">Текущий ордер:</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-400">Тип:</span>
                            <Badge className="ml-2 bg-blue-500">
                              {session.current_order_type === 'entry' ? 'Entry' : 'Take Profit'}
                            </Badge>
                          </div>
                          <div>
                            <span className="text-gray-400">Order ID:</span>
                            <span className="text-white ml-2 font-mono text-xs">
                              {session.current_order_id.substring(0, 12)}...
                            </span>
                          </div>
                        </div>
                        {session.tp_order_id && (
                          <div className="mt-2">
                            <span className="text-gray-400 text-sm">TP Order:</span>
                            <span className="text-[#00ff88] ml-2 font-mono text-xs">
                              {session.tp_order_id === 'market_pending' ? 'Ожидает цены' : session.tp_order_id.substring(0, 12) + '...'}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Steps Overview */}
                    <div className="space-y-2">
                      <h4 className="text-white font-semibold mb-2">Шаги сетапа:</h4>
                      {session.setup?.steps?.map((step, index) => (
                        <div
                          key={index}
                          className={`flex items-center gap-3 p-3 rounded-lg ${
                            index === session.current_step_index
                              ? 'bg-yellow-500/10 border border-yellow-500/30'
                              : index < session.current_step_index
                              ? 'bg-green-500/10 border border-green-500/30'
                              : 'bg-gray-900/50 border border-gray-800'
                          }`}
                        >
                          <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
                            {index < session.current_step_index ? (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            ) : index === session.current_step_index ? (
                              <Clock className="h-5 w-5 text-yellow-500" />
                            ) : (
                              <span className="text-gray-400 text-sm">{index + 1}</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              {step.direction === 'long' ? (
                                <TrendingUp className="h-4 w-4 text-green-400" />
                              ) : (
                                <TrendingDown className="h-4 w-4 text-red-400" />
                              )}
                              <span className="text-white font-semibold">
                                {step.direction === 'long' ? 'Long' : 'Short'}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-400">Entry: ${step.entry_price.toLocaleString()}</p>
                            <p className="text-sm text-gray-400">TP: ${step.exit_price.toLocaleString()}</p>
                            {step.leverage && (
                              <p className="text-sm text-[#00ff88] font-semibold">{step.leverage}x плечо</p>
                            )}
                            {step.liquidation_price && (
                              <p className="text-xs text-red-400">
                                Liq: ${step.liquidation_price.toLocaleString()} ({step.p_liq_percent?.toFixed(2)}%)
                              </p>
                            )}
                          </div>
                          {getStepStatusBadge(index, session.current_step_index)}
                        </div>
                      ))}
                    </div>

                    {/* View Logs Button */}
                    <Button
                      onClick={() => {
                        setSelectedSession(session);
                        fetchSessionLogs(session.id);
                      }}
                      className="mt-4 bg-gray-700 hover:bg-gray-600 w-full"
                    >
                      Посмотреть логи
                    </Button>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Session Logs Modal */}
        {selectedSession && sessionLogs && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
            <Card className="bg-[#1a1a1a] border-gray-800 max-w-4xl w-full max-h-[80vh] overflow-hidden">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">Логи сессии: {selectedSession.setup?.title}</CardTitle>
                  <Button
                    onClick={() => {
                      setSelectedSession(null);
                      setSessionLogs(null);
                    }}
                    className="bg-gray-700 hover:bg-gray-600"
                  >
                    Закрыть
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="overflow-y-auto max-h-[60vh]">
                <div className="space-y-2">
                  {sessionLogs.step_history && sessionLogs.step_history.length > 0 ? (
                    sessionLogs.step_history.map((log, index) => (
                      <div key={index} className="bg-gray-900/50 rounded p-3 text-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-gray-400">
                            {new Date(log.timestamp).toLocaleString('ru-RU')}
                          </span>
                          <Badge className="bg-blue-500">Step {log.step_index + 1}</Badge>
                          <Badge className={
                            log.event.includes('filled') || log.event.includes('executed')
                              ? 'bg-green-500'
                              : log.event.includes('failed')
                              ? 'bg-red-500'
                              : 'bg-gray-500'
                          }>
                            {log.event}
                          </Badge>
                        </div>
                        <pre className="text-gray-300 text-xs overflow-x-auto">
                          {JSON.stringify(log, null, 2)}
                        </pre>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-400 text-center py-8">Логов пока нет</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default TradingMonitor;
